import React from 'react';
import { Chip, useTheme } from '@mui/material';
import { keyframes } from '@emotion/react';

const pulse = keyframes`
  0% {
    box-shadow: 0 0 0 0 rgba(0, 255, 136, 0.7);
  }
  70% {
    box-shadow: 0 0 0 6px rgba(0, 255, 136, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(0, 255, 136, 0);
  }
`;

const errorPulse = keyframes`
  0% {
    box-shadow: 0 0 0 0 rgba(255, 68, 68, 0.7);
  }
  70% {
    box-shadow: 0 0 0 6px rgba(255, 68, 68, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(255, 68, 68, 0);
  }
`;

const warningPulse = keyframes`
  0% {
    box-shadow: 0 0 0 0 rgba(255, 170, 0, 0.7);
  }
  70% {
    box-shadow: 0 0 0 6px rgba(255, 170, 0, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(255, 170, 0, 0);
  }
`;

function StatusBadge({ status }) {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  const getStatusConfig = () => {
    switch (status) {
      case 'running':
        return {
          color: isDark ? '#00ff88' : '#4caf50',
          bgColor: isDark ? 'rgba(0, 255, 136, 0.1)' : 'rgba(76, 175, 80, 0.1)',
          borderColor: isDark ? '#00ff88' : '#4caf50',
          animation: pulse,
          label: 'Running'
        };
      case 'idle':
        return {
          color: isDark ? '#b3b3b3' : '#666666',
          bgColor: isDark ? 'rgba(179, 179, 179, 0.1)' : 'rgba(102, 102, 102, 0.1)',
          borderColor: isDark ? '#b3b3b3' : '#666666',
          animation: null,
          label: 'Idle'
        };
      case 'starting':
        return {
          color: isDark ? '#ffaa00' : '#ff9800',
          bgColor: isDark ? 'rgba(255, 170, 0, 0.1)' : 'rgba(255, 152, 0, 0.1)',
          borderColor: isDark ? '#ffaa00' : '#ff9800',
          animation: warningPulse,
          label: 'Starting'
        };
      case 'stopping':
        return {
          color: isDark ? '#ffaa00' : '#ff9800',
          bgColor: isDark ? 'rgba(255, 170, 0, 0.1)' : 'rgba(255, 152, 0, 0.1)',
          borderColor: isDark ? '#ffaa00' : '#ff9800',
          animation: warningPulse,
          label: 'Stopping'
        };
      case 'error':
        return {
          color: isDark ? '#ff4444' : '#f44336',
          bgColor: isDark ? 'rgba(255, 68, 68, 0.1)' : 'rgba(244, 67, 54, 0.1)',
          borderColor: isDark ? '#ff4444' : '#f44336',
          animation: errorPulse,
          label: 'Error'
        };
      case 'scheduled':
        return {
          color: isDark ? '#66b3ff' : '#2196f3',
          bgColor: isDark ? 'rgba(102, 179, 255, 0.1)' : 'rgba(33, 150, 243, 0.1)',
          borderColor: isDark ? '#66b3ff' : '#2196f3',
          animation: null,
          label: 'Scheduled'
        };
      default:
        return {
          color: isDark ? '#b3b3b3' : '#666666',
          bgColor: isDark ? 'rgba(179, 179, 179, 0.1)' : 'rgba(102, 102, 102, 0.1)',
          borderColor: isDark ? '#b3b3b3' : '#666666',
          animation: null,
          label: status.charAt(0).toUpperCase() + status.slice(1)
        };
    }
  };

  const config = getStatusConfig();

  return (
    <Chip
      label={config.label}
      size="small"
      data-testid="status-badge"
      sx={{
        color: config.color,
        backgroundColor: config.bgColor,
        border: `1px solid ${config.borderColor}`,
        fontWeight: 600,
        fontSize: '0.75rem',
        height: 24,
        animation: config.animation ? `${config.animation} 2s infinite` : 'none',
        backdropFilter: 'blur(10px)',
        transition: 'all 0.3s ease-in-out',
        '&:hover': {
          transform: 'scale(1.05)',
          boxShadow: `0 0 12px ${config.color}40`,
        },
        '& .MuiChip-label': {
          padding: '0 8px',
          textShadow: isDark ? `0 0 8px ${config.color}80` : 'none',
        },
      }}
    />
  );
}

export default StatusBadge;